/**
 * Payment Proof Panel
 *
 * Side panel listing every slip attached to a payment: images open in the lightbox,
 * anything else (PDF) downloads. Kept out of PaymentDetailCard so the detail page can put
 * the proof in its own column next to the payment facts, while the verification queue keeps
 * showing everything in one card.
 */

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Download, ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import {
  adminPaymentService,
  PaymentDetailDto,
} from "@/services/admin-payment.service";
import {
  adminUploadService,
  UploadedFile,
} from "@/services/admin-upload.service";
import {
  isRenderableImage,
  proofDownloadPath,
  proofFileNameOf,
} from "@/lib/paymentProof";
import {
  ACCEPTED_UPLOAD_TYPES,
  MAX_UPLOAD_SIZE_BYTES,
  partitionValidUploads,
} from "@/lib/uploadValidation";
import { AuthedImage } from "./AuthedImage";
import { PaymentProofLightbox } from "./PaymentProofLightbox";
import { awaitRefresh } from "@/lib/awaitRefresh";

const MAX_PROOF_FILES = 10;

interface PaymentProofPanelProps {
  payment: PaymentDetailDto;
  className?: string;
  /**
   * Mirrors "an upload or a delete is in flight" up to the page, so the Verify/Reject/Refund
   * actions can disable themselves for the length of it. Settling one of those while a proof
   * attach/remove is still running would let the operator act on a proof list that is about to
   * change under them.
   */
  onBusyChange?: (busy: boolean) => void;
}

/** Pulls the file down through the authorised client, then hands it to the browser. */
async function downloadAttachment(url: string, fileName: string) {
  try {
    const res = await apiClient.get(url, { responseType: "blob" });
    const objectUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    toast.error("Failed to download file");
  }
}

async function uploadFiles(files: File[]) {
  const results = await Promise.allSettled(
    files.map((file) => adminUploadService.uploadFile(file)),
  );

  return {
    uploaded: results
      .filter(
        (result): result is PromiseFulfilledResult<UploadedFile> =>
          result.status === "fulfilled",
      )
      .map((result) => result.value),
    failed: results.filter(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    ),
  };
}

async function removeUnusedUploads(files: UploadedFile[]) {
  await Promise.allSettled(
    files.map((file) =>
      adminUploadService.removeUnusedUpload(file.attachmentId),
    ),
  );
}

export function PaymentProofPanel({
  payment,
  className,
  onBusyChange,
}: PaymentProofPanelProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    refId: string;
    fileName: string;
  } | null>(null);

  useEffect(() => {
    onBusyChange?.(uploading || deleting);
  }, [uploading, deleting, onBusyChange]);

  // Released on unmount as well. The parent holds Verify, Reject and Refund on this flag, so a panel
  // that went away mid-upload would otherwise leave them disabled until the page itself remounted.
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  // New proof can only be added while the payment is still waiting for verification.
  const canAddProof =
    payment.status === "Pending" || payment.status === "AwaitingVerification";

  // A confirmed payment keeps its proof. Other statuses may still remove it.
  const canDeleteProof = payment.status !== "Completed";

  // Invalidates the whole `["payments"]` prefix, not just this payment's own detail key: an
  // attach/remove changes whether the row has proof, which the list table and the verification
  // queue both render, and only the detail key was refreshed before — the list and queue kept
  // showing the old proof count until an unrelated refetch happened to land.
  //
  // Wrapped in `awaitRefresh` at each call site (not here): the write itself already succeeded by
  // the time this runs, so a stalled re-read must not hold the upload/delete busy flag — and the
  // overlay it drives — up for as long as the api client's own timeout.
  const refreshPayment = () =>
    queryClient.invalidateQueries({ queryKey: ["payments"] });

  const handleFilesPicked = async (fileList: FileList | null) => {
    const picked = Array.from(fileList ?? []);
    if (picked.length === 0) return;

    const availableSlots = MAX_PROOF_FILES - (payment.attachments?.length ?? 0);
    if (availableSlots <= 0) {
      toast.error(`A payment can have at most ${MAX_PROOF_FILES} proof files.`);
      return;
    }

    const files = picked.slice(0, availableSlots);
    if (picked.length > files.length) {
      // Never drop files silently — the operator picked them and would assume they went up.
      toast.warning(
        `Only ${files.length} file(s) were selected because the payment limit is ${MAX_PROOF_FILES}.`,
      );
    }

    // Screen out what the API would refuse anyway, so an oversized or wrong-format file
    // costs a toast rather than a round trip ending in a raw server error.
    const { valid, errors } = partitionValidUploads(files);
    errors.forEach((message) => toast.error(message));
    if (valid.length === 0) return;

    setUploading(true);
    try {
      // One bad file must not strand the ones that uploaded fine: attach every success, then
      // report the failures. A file uploaded but never attached is an orphan nobody can see.
      const { uploaded, failed } = await uploadFiles(valid);

      if (uploaded.length > 0) {
        try {
          await adminPaymentService.attachPaymentProof(
            payment.paymentId,
            uploaded.map((file) => file.attachmentId),
          );
        } catch (error) {
          await removeUnusedUploads(uploaded);
          throw error;
        }
        toast.success(
          uploaded.length > 1
            ? `${uploaded.length} proof files attached`
            : "Proof attached",
        );
        await awaitRefresh(refreshPayment());
      }

      if (failed.length > 0) {
        const firstError = failed[0].reason;
        toast.error(
          firstError instanceof Error
            ? firstError.message
            : `${failed.length} file(s) could not be uploaded`,
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to attach proof",
      );
    } finally {
      setUploading(false);
      // Clear the input so picking the same file twice still fires a change event.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    // Captured up front: `pendingDelete` is a piece of state, and the confirm this delete belongs
    // to is guarded shut (below) for the length of the request, so nothing should be able to
    // repoint it at a different file mid-flight. Comparing against this local rather than the
    // live state is a second lock on that, not a reaction to an observed bug.
    const target = pendingDelete;
    setDeleting(true);
    try {
      await adminPaymentService.removePaymentProof(target.refId);
      toast.success("Proof removed");
      // Refetch before closing, not after: the dialog closing is what lets the operator act
      // again, and it must not do that against a proof list that has not caught up yet. Capped
      // by `awaitRefresh` so a stalled re-read cannot leave the confirm (and its overlay) open
      // for the removal's own API timeout.
      await awaitRefresh(refreshPayment());
      setPendingDelete((current) =>
        current?.refId === target.refId ? null : current,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to remove proof",
      );
    } finally {
      setDeleting(false);
    }
  };

  const attachments = payment.attachments ?? [];
  const legacyProofUrl = payment.paymentProofUrl
    ? proofDownloadPath(payment.paymentProofUrl)
    : null;
  const legacyIsImage = isRenderableImage({
    fileName: payment.paymentProofUrl,
  });

  const lightboxImages = [
    ...attachments
      .filter((a) => a.contentType.startsWith("image/"))
      .map((a) => ({
        src: proofDownloadPath(a.downloadUrl),
        alt: a.fileName,
        fileName: a.fileName,
      })),
    ...(legacyProofUrl && legacyIsImage
      ? [
          {
            src: legacyProofUrl,
            alt: "Payment proof",
            fileName: proofFileNameOf(legacyProofUrl),
          },
        ]
      : []),
  ];

  const openLightbox = (src: string) => {
    const index = lightboxImages.findIndex((image) => image.src === src);
    if (index < 0) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const hasProof = attachments.length > 0 || !!legacyProofUrl;

  return (
    <Card className={className}>
      <CardHeader className="p-4 pb-2 sm:p-5 sm:pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <ImageIcon className="h-4 w-4 text-primary" />
            Payment Proof
          </CardTitle>
          {attachments.length > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {attachments.length}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-3 sm:p-5 sm:pt-3">
        {!hasProof && (
          <p className="text-sm text-muted-foreground">
            No payment proof has been uploaded for this payment.
          </p>
        )}

        {attachments.map((attachment) =>
          attachment.contentType.startsWith("image/") ? (
            <div key={attachment.refId} className="relative">
              <button
                type="button"
                className="w-full cursor-pointer text-left"
                onClick={() =>
                  openLightbox(proofDownloadPath(attachment.downloadUrl))
                }
              >
                <AuthedImage
                  src={proofDownloadPath(attachment.downloadUrl)}
                  alt={attachment.fileName}
                  className="max-h-64 w-full rounded-lg border bg-muted object-contain"
                />
              </button>
              {canDeleteProof && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-2 top-2 h-7 w-7 text-destructive shadow-sm"
                  aria-label={`Remove ${attachment.fileName}`}
                  disabled={uploading || deleting}
                  onClick={() =>
                    setPendingDelete({
                      refId: attachment.refId,
                      fileName: attachment.fileName,
                    })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ) : (
            <div
              key={attachment.refId}
              className="flex items-center gap-2 rounded-lg border p-3 transition-colors hover:bg-muted/50"
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                onClick={() =>
                  downloadAttachment(
                    proofDownloadPath(attachment.downloadUrl),
                    attachment.fileName,
                  )
                }
              >
                <Download className="h-4 w-4 shrink-0" />
                <span className="truncate text-sm font-medium">
                  {attachment.fileName}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {(attachment.fileSize / 1024).toFixed(1)} KB
                </span>
              </button>
              {canDeleteProof && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive"
                  aria-label={`Remove ${attachment.fileName}`}
                  disabled={uploading || deleting}
                  onClick={() =>
                    setPendingDelete({
                      refId: attachment.refId,
                      fileName: attachment.fileName,
                    })
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ),
        )}

        {/* Legacy single-file proof, kept for payments created before attachments existed */}
        {attachments.length === 0 &&
          legacyProofUrl &&
          (!legacyIsImage ? (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
              onClick={() =>
                downloadAttachment(
                  legacyProofUrl,
                  proofFileNameOf(legacyProofUrl),
                )
              }
            >
              <Download className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium">
                Download Payment Proof
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="w-full cursor-pointer text-left"
              onClick={() => openLightbox(legacyProofUrl)}
            >
              <AuthedImage
                src={legacyProofUrl}
                alt="Payment proof"
                className="max-h-64 w-full rounded-lg border bg-muted object-contain"
              />
            </button>
          ))}

        {canAddProof && (
          <>
            {hasProof && <Separator />}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Files are added to the payment; a Pending payment moves to
                Awaiting Verification once proof is attached. PNG, JPG or PDF,
                up to {MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)}MB each.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_UPLOAD_TYPES}
                className="hidden"
                onChange={(e) => handleFilesPicked(e.target.files)}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={uploading || deleting}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Add payment proof
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          // A delete in flight owns this confirm: the request has no way to report back once
          // the dialog naming the file is gone.
          if (deleting && !open) return;
          setPendingDelete(open ? pendingDelete : null);
        }}
      >
        <AlertDialogContent
          onEscapeKeyDown={(event) => {
            if (deleting) event.preventDefault();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this proof file?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {pendingDelete?.fileName} will be removed from this payment. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {deleting ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PaymentProofLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />

      {/* No page-blocking overlay for an upload: it can run up to 300s attach + 90s cleanup, and
          locking the whole console for minutes over one file picker is worse than the alternative
          — the upload button already shows its own "Uploading…" spinner, and Verify/Reject/Refund
          disable themselves through `onBusyChange` for as long as this is true. Delete keeps the
          overlay: it is a small, bounded request behind its own confirm dialog. */}
      <BusyOverlay open={deleting} label="Removing proof…" />
    </Card>
  );
}
